<template>
  <div class="forum_square">
    <div class="forum_square__header">
      <h1>论坛</h1>
      <button type="button" class="forum_square__message" @click="goMessages">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 5.5A3.5 3.5 0 0 1 7.5 2h9A3.5 3.5 0 0 1 20 5.5v7A3.5 3.5 0 0 1 16.5 16H10l-4.2 3.15A1 1 0 0 1 4 18.35V5.5z" />
        </svg>
        <span>消息</span>
        <em v-if="hasUnreadMessage" />
      </button>
    </div>

    <div class="forum_square__profile" @click="goProfile">
      <div class="forum_square__avatar">
        <img :src="me.avatar || FORUM_DEFAULT_AVATAR" alt="" />
      </div>
      <div class="forum_square__profile_body">
        <div class="forum_square__profile_row">
          <span class="forum_square__nickname text_ellipsis">{{ me.nickname || '昵称为空' }}</span>
          <span class="forum_square__edit">编辑</span>
        </div>
        <div v-if="boundAccountName" class="forum_square__bind_account text_ellipsis">已绑定账号：{{ boundAccountName }}</div>
      </div>
      <span class="forum_square__arrow">›</span>
    </div>

    <div class="forum_square__divider" />

    <div class="forum_square__section">
      <div class="forum_square__title">我的论坛</div>
      <div v-if="joinedForums.length" class="joined_forum__list hide-scroll">
        <div v-for="g in joinedForums" :key="g.groupId" class="joined_forum__item" @click="onEnterGroup(g)">
          <div class="forum_square__group_icon">
            <img v-if="g.groupIcon" :src="g.groupIcon" alt="" />
            <div v-else class="forum_square__group_placeholder">
              {{ (g.groupName || '')[0] }}
            </div>
          </div>
          <div class="joined_forum__name">{{ g.groupName }}</div>
        </div>
      </div>
      <div v-else class="forum_square__empty_text">暂无订阅数据，赶快订阅去吧！</div>
    </div>

    <div class="forum_square__divider" />

    <div class="forum_square__section">
      <div class="forum_square__title">热门论坛</div>
      <div v-if="hotForums.length" class="hot_forum__list">
        <div v-for="g in hotForums" :key="g.groupId" class="hot_forum__item" @click="onHotRowClick(g)">
          <div class="forum_square__group_icon forum_square__group_icon--hot">
            <img v-if="g.groupIcon" :src="g.groupIcon" alt="" />
            <div v-else class="forum_square__group_placeholder">
              {{ (g.groupName || '')[0] }}
            </div>
          </div>
          <div class="hot_forum__content">
            <div class="hot_forum__name text_ellipsis">{{ g.groupName }}</div>
            <div class="hot_forum__meta">
              <span class="hot_forum__count">{{ g.peopleNum || 0 }}</span>
              <span>成员</span>
            </div>
            <div class="hot_forum__desc text_ellipsis">{{ g.description }}</div>
          </div>
          <button
            type="button"
            :class="['hot_forum__button', { 'hot_forum__button--joined': g.joined }]"
            @click="onHotActionClick(g, $event)"
          >{{ g.joined ? '已加入' : '加入' }}</button>
        </div>
      </div>
      <div v-else-if="!loading" class="forum_empty">
        <div class="forum_empty__icon">暂无</div>
        <div class="forum_empty__text">暂无论坛</div>
      </div>
    </div>
  </div>
</template>

<script>
import { Dialog, Toast } from 'vant';
import { mapState } from 'vuex';
import { getCommunityUnreadMsgCount, getForumList, getUserCenter, joinOrQuitGroup } from '@/api/forum';
import { FORUM_DEFAULT_AVATAR } from './constants';
import trackingMixin, { EVENT_TYPE_ENUM } from './mixins/tracking';
import forumLoginRefreshMixin from './mixins/forumLoginRefresh';

export default {
  name: 'ForumSquare',
  mixins: [trackingMixin, forumLoginRefreshMixin],
  data() {
    return {
      FORUM_DEFAULT_AVATAR,
      me: {},
      joinedForums: [],
      hotForums: [],
      loading: false,
      joiningMap: {},
      hasFetched: false,
      unreadCount: 0,
    };
  },
  computed: {
    ...mapState('account', {
      accountUserInfo: (state) => state.userInfo || {},
    }),
    hasUnreadMessage() {
      return Number(this.unreadCount || 0) > 0;
    },
    boundAccountName() {
      const info = this.accountUserInfo || {};
      return info.game_account || '';
    },
  },
  activated() {
    if (!this.hasFetched) return;
    this.fetchAll();
  },
  created() {
    this.fetchAll();
  },
  methods: {
    getForumLoginRefreshConfig() {
      return {
        routeName: 'forum-square',
        onRefresh: () => this.fetchAll(),
      };
    },
    async fetchAll() {
      this.loading = true;
      try {
        const [centerRes, listRes, unreadRes] = await Promise.all([
          getUserCenter(),
          getForumList(),
          getCommunityUnreadMsgCount(),
        ]);
        if (centerRes && centerRes.data) {
          this.me = { ...centerRes.data };
        }
        this.unreadCount = this.getUnreadCount(unreadRes && unreadRes.data);
        const listData = (listRes && listRes.data) || {};
        this.joinedForums = listData.joinedForums || [];
        this.hotForums = listData.hotForums || [];
        this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_square_page_show, {
          page_type: '论坛广场',
        });
      } finally {
        this.hasFetched = true;
        this.loading = false;
      }
    },
    getUnreadCount(data) {
      if (data && typeof data === 'object') {
        return Number(data.count || data.total || data.unreadCount || data.num || 0);
      }
      return Number(data || 0);
    },
    goProfile() {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_square_button, {
        page_type: '论坛广场',
        button_type: '用户信息热区',
      });
      this.$router.push({ name: 'forum-profile' });
    },
    goMessages() {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_square_button, {
        page_type: '论坛广场',
        button_type: '消息入口',
      });
      this.$router.push({ name: 'forum-messages' });
    },
    onEnterGroup(group, pageType = '我的论坛') {
      this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_info_button, {
        page_type: pageType,
        group_id: group.groupId,
        group_name: group.groupName || '',
        button_type: '进入论坛',
      });
      this.$router.push({ name: 'forum-group', params: { groupId: group.groupId } });
    },
    /** 点击热门论坛卡片（非按钮区域）：不论是否已加入，均进入论坛首页 */
    onHotRowClick(group) {
      this.onEnterGroup(group, '热门论坛');
    },
    /** 热门论坛右侧按钮：未加入则加入；已加入则确认后退出 */
    async onHotActionClick(group, event) {
      event.stopPropagation();
      if (this.joiningMap[group.groupId]) {
        Toast('客官你手速太快啦，请慢点');
        return;
      }
      if (group.joined) {
        this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_info_button, {
          page_type: '热门论坛',
          group_id: group.groupId,
          group_name: group.groupName || '',
          button_type: '退出论坛',
        });
        try {
          await Dialog.confirm({
            title: '提示',
            message: '确定退出该论坛？退出后将不再在\'我加入的\'中展示',
          });
        } catch (e) {
          return;
        }
        this.$set(this.joiningMap, group.groupId, true);
        try {
          const res = await joinOrQuitGroup(group.groupId, 2);
          if (res && res.success) {
            Toast.success('已退出');
            this.fetchAll();
          }
        } catch (e) {
          // 接口失败提示由 axios 拦截器统一处理
        } finally {
          this.$delete(this.joiningMap, group.groupId);
        }
        return;
      }
      this.$set(this.joiningMap, group.groupId, true);
      try {
        this.EVENT_TRACKING(EVENT_TYPE_ENUM.platform_group_info_button, {
          page_type: '热门论坛',
          group_id: group.groupId,
          group_name: group.groupName || '',
          button_type: '加入论坛',
        });
        const res = await joinOrQuitGroup(group.groupId, 1);
          if (res && res.success) {
            Toast.success('订阅成功');
            this.fetchAll();
          }
        } catch (e) {
          // 接口失败提示由 axios 拦截器统一处理
        } finally {
        this.$delete(this.joiningMap, group.groupId);
      }
    },
  },
};
</script>

<style scoped>
.forum_square {
  min-height: 100vh;
  background: #fefefe;
}

.forum_square__header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  padding: 0 16px;
  background: #fff;
}

.forum_square__header h1 {
  margin: 0;
  color: #111827;
  font-size: 17px;
  font-weight: 600;
}

.forum_square__message {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 8px;
  border: 0;
  border-radius: 15px;
  color: #374151;
  background: #f6f7f9;
  font-size: 12px;
}

.forum_square__message svg {
  width: 16px;
  height: 16px;
  fill: currentColor;
}

.forum_square__message em {
  position: absolute;
  top: 2px;
  right: 4px;
  width: 7px;
  height: 7px;
  border: 1px solid #fff;
  border-radius: 50%;
  background: #fd383c;
}

.forum_square__profile {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 16px 32px;
  background: #fff;
}

.forum_square__profile:active,
.hot_forum__item:active {
  opacity: 0.8;
}

.forum_square__avatar {
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
  background: #e5e7eb;
}

.forum_square__avatar img,
.forum_square__group_icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.forum_square__avatar_placeholder,
.forum_square__group_placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #999;
  font-weight: 700;
}

.forum_square__avatar_placeholder {
  font-size: 24px;
}

.forum_square__profile_body {
  min-width: 0;
  flex: 1;
}

.forum_square__profile_row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.forum_square__nickname {
  color: #111827;
  font-size: 14px;
  font-weight: 500;
}

.forum_square__edit {
  flex-shrink: 0;
  color: #0249ff;
  font-size: 10px;
}

.forum_square__bind_account {
  margin-top: 6px;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.2;
}

.forum_square__arrow {
  flex-shrink: 0;
  color: #d1d5db;
  font-size: 18px;
}

.forum_square__divider {
  height: 5px;
  background: #f5f5f5;
}

.forum_square__section {
  padding: 16px 12px;
  background: #fff;
}

.forum_square__title {
  margin-bottom: 24px;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
}

.forum_square__group_icon {
  width: 52px;
  height: 52px;
  overflow: hidden;
  border-radius: 12px;
  background: #f3f4f6;
}

.forum_square__group_icon--hot {
  flex-shrink: 0;
}

.forum_square__group_placeholder {
  font-size: 18px;
}

.forum_square__empty_text {
  color: #999;
  font-size: 12px;
}

.forum_empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 96px 24px;
  color: #999;
  text-align: center;
}

.forum_empty__icon {
  margin-bottom: 24px;
  font-size: 48px;
  line-height: 1;
}

.forum_empty__text {
  font-size: 14px;
  line-height: 20px;
}

.joined_forum__list {
  display: flex;
  overflow-x: auto;
  white-space: nowrap;
  padding: 0 8px;
}

.joined_forum__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 72px;
}

.joined_forum__item + .joined_forum__item {
  margin-left: 16px;
}

.joined_forum__name {
  width: 72px;
  margin-top: 6px;
  color: #374151;
  font-size: 11px;
  line-height: 14px;
  text-align: center;
  white-space: normal;
  word-break: break-all;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.hot_forum__item {
  display: flex;
  align-items: center;
  padding: 12px 0;
}

.hot_forum__item + .hot_forum__item {
  border-top: 1px solid #f2f2f2;
}

.hot_forum__content {
  width: 0;
  min-width: 0;
  flex: 1;
  margin-left: 12px;
  margin-right: 12px;
  overflow: hidden;
}

.hot_forum__name {
  color: #111827;
  font-size: 13px;
  font-weight: 600;
}

.hot_forum__meta,
.hot_forum__desc {
  margin-top: 4px;
  color: #999;
  font-size: 12px;
}

.hot_forum__desc {
  width: 100%;
  line-height: 18px;
}

.hot_forum__count {
  color: #ff7a2a;
}

.hot_forum__button {
  width: 58px;
  height: 26px;
  flex-shrink: 0;
  border: 0;
  border-radius: 13px;
  color: #fff;
  background: #fd383c;
  font-size: 10px;
  font-weight: 500;
  line-height: 26px;
  text-align: center;
}

.hot_forum__button--joined {
  background: #6879ad;
}

.hide-scroll::-webkit-scrollbar {
  display: none;
}
</style>
